import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle, Clock, CircleDot, Briefcase, Trash2,
} from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { kycBadge, riskBadge } from "./constants";

const getDisplayName = (c) => {
  if (c.person_type === "moral") return c.legal_name || "—";
  return [c.first_name, c.paternal_surname, c.maternal_surname]
    .filter(Boolean)
    .join(" ") || "—";
};

export function CustomerTableRow({ customer: c, isSelected, onToggle, onDelete }) {
  const kyc = kycBadge[c.kyc_status] || kycBadge.pending;
  const risk = riskBadge[c.risk_level] || riskBadge.low;
  const isMoral = c.person_type === "moral";
  const hasMinData = isMoral ? !!c.legal_name : (!!c.first_name && !!c.paternal_surname);
  const hasRfc = !!c.rfc;
  const isDraft = !!c.is_draft;
  const isIncomplete = isDraft || !hasMinData || !hasRfc;
  // Drafts resume in the phased-onboarding flow; customers link
  // to their detail page.
  const rowHref = isDraft
    ? `/customers/new?draft_id=${c.draft_id || c.customer_id}`
    : `/customers/${c.customer_id}`;

  return (
    <tr
      className="hover:bg-slate-800/50 transition-colors cursor-pointer"
      data-testid={`customer-row-${c.customer_id}`}
    >
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(c.customer_id)}
          aria-label={`Seleccionar ${getDisplayName(c)}`}
          data-testid={`select-checkbox-${c.customer_id}`}
          title={isDraft
            ? "Borrador — solo se puede eliminar en lote (screening/PDF/export no aplican)"
            : undefined}
        />
      </td>
      <td className="px-5 py-3">
        <Link to={rowHref} className="text-sm text-teal-400 hover:text-teal-300 font-mono">
          {isDraft ? "Borrador" : c.customer_number}
        </Link>
      </td>
      <td className="px-5 py-3">
        <Link to={rowHref} className="text-sm text-slate-200 hover:text-white">
          {getDisplayName(c)}
        </Link>
      </td>
      <td className="px-5 py-3">
        <span className={`text-sm flex items-center gap-1.5 ${isMoral ? "text-blue-400" : "text-slate-400"}`}>
          {isMoral && <Briefcase size={12} />}
          {c.person_type ? (isMoral ? "Moral" : "Fisica") : "—"}
        </span>
      </td>
      <td className="px-5 py-3 text-sm text-slate-400 font-mono">{c.rfc || "—"}</td>
      <td className="px-5 py-3">
        <div className="flex items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${kyc.cls}`}>
            {c.kyc_status === "verified" ? <CheckCircle size={12} /> : <Clock size={12} />}
            {kyc.label}
          </span>
          {isIncomplete && !isDraft && c.kyc_status !== "verified" && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-red-500/10 text-red-400 border border-red-500/20"
              data-testid={`incomplete-badge-${c.customer_id}`}
            >
              <CircleDot size={10} /> Incompleto
            </span>
          )}
        </div>
      </td>
      <td className="px-5 py-3">
        {isDraft ? (
          <span className="text-xs text-slate-500">—</span>
        ) : (
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${risk.cls}`}>
            {risk.label}
          </span>
        )}
      </td>
      <td className="px-5 py-3 text-sm text-slate-500">
        {c.created_at ? new Date(c.created_at).toLocaleDateString("es-MX") : "—"}
      </td>
      <td className="px-5 py-3">
        <button
          onClick={(e) => onDelete(c, e)}
          data-testid={`delete-btn-${c.customer_id}`}
          className="p-1.5 hover:bg-red-500/10 rounded-lg text-slate-500 hover:text-red-400 transition-colors"
          title={isDraft ? "Eliminar borrador" : "Eliminar cliente"}
        >
          <Trash2 size={16} />
        </button>
      </td>
    </tr>
  );
}
